export type EnemyKind = 'rat' | 'rat_king' | 'trash_lobber' | 'pizza_rat' | 'pigeon' | 'dive_pigeon' | 'golden_pigeon' | 'sewer_elite';

/**
 * 'ground' walks the street via `NavGrid` pathing, snaps to floor height,
 * uses the shared telegraph/attack (melee or ranged) resolution.
 * 'flyer' holds a fixed altitude above its home ground height, ignores
 * floors entirely (never snaps down), still collides with walls; uses the
 * SAME shared telegraph/attack resolution as 'ground' (a peck is just an
 * in-place lunge, exactly like a rat's bite).
 * 'diver' is its own state machine (idle-hover -> telegraph -> a committed
 * high-speed dive -> a vulnerable recover climb) — ai/enemy.ts's
 * `updateDiver`, not the shared resolution.
 * 'burrower' (C08/Wave M, PLAN-CITY2 §5 M-3) is also its own state machine
 * (telegraph -> burrow/hidden -> a submerged hold -> emerge with a single
 * hit check at its own final frame) — ai/enemy.ts's `updateBurrower`. It
 * never chases; it waits at home and strikes via the burrow/emerge cycle.
 */
export type MovementMode = 'ground' | 'flyer' | 'diver' | 'burrower';

export interface EnemyArchetype {
  kind: EnemyKind;
  label: string;
  /** Manifest asset id to instantiate via `core/assets.ts`, or null for a primitive fallback (§0.4 — gray-box geometry is code, not art). */
  assetId: string | null;
  scale: number;
  /** Multiply-tint applied to cloned materials (Classic gotcha #62/#64's shared-asset reskin pattern) — null = no tint. */
  tint: number | null;
  maxHp: number;
  moveSpeed: number;
  radius: number;
  aggroRange: number;
  attackRange: number;
  telegraphDuration: number;
  attackCooldown: number;
  contactDamage: number;
  /** 0..1 — fraction of incoming knockback force ABSORBED (0 = full knockback, 1 = immovable). */
  knockbackResist: number;
  ranged: boolean;
  preferredMinRange: number;
  preferredMaxRange: number;
  projectileDamage: number;
  projectileSpeed: number;
  /** Super-meter/feedback weighting only — NOT currency (§0.8: no loot/progression yet). */
  scoreValue: number;

  movementMode: MovementMode;
  /** Gates the shared telegraph/attack trigger on `BlockPhysics.segmentClear` — the ranged kiter and the dive-bomb ambusher both refuse to commit to an attack they can't actually see. Melee chasers don't need it (contact range already implies visibility). */
  requiresLineOfSight: boolean;
  /** Distance from HOME (the spawn/habitat point, not the player) beyond which an active enemy gives up and returns, instead of chasing indefinitely off its own habitat. */
  leashRange: number;
  /** 'flyer'/'diver' only: world-Y offset ABOVE the habitat's `groundHint` this archetype holds while hovering. Irrelevant for 'ground'. */
  flightHeight: number;
  /** 'diver' only: cruise speed during the committed dive (vs. `moveSpeed`, its normal hover-drift speed). */
  diveSpeed: number;
  /** 'diver' only: max seconds a dive run lasts before giving up and recovering even on a miss. */
  diveDuration: number;
  /** 'diver' only: seconds spent climbing back to hover altitude — a natural, un-defended cooldown window before it can dive again. */
  recoverDuration: number;
  /** S11: seconds a habitat SLOT waits after this archetype's kill/despawn before its next respawn attempt — ai/spawner.ts reads this per-instance (`enemy.archetype.respawnDelay`) instead of the old flat `RESPAWN_DELAY` constant, so a genuinely rare kind (golden_pigeon) can sit out far longer than the roster's normal population-refresh cadence without a second timing system. */
  respawnDelay: number;
  /**
   * S11 review fix (MEDIUM, spec-fidelity): 0 for every pre-S11 archetype
   * (identical behavior — `ai/enemy.ts`'s idle branch still calls the
   * existing tiny-wobble `hoverInPlace()` unchanged). A positive value
   * (golden_pigeon only, so far) makes idle instead drift to a periodically
   * re-picked random point within this radius of home via a NEW `wanderIdle`
   * path — closing a real gap the review caught: with `aggroRange:0`,
   * golden_pigeon never left `updateChaseOrIdle`'s idle branch, so without
   * this field "a rare ROAMER" was in practice a stationary hover-bob at one
   * fixed point, indistinguishable from any other flyer's idle animation.
   */
  wanderRadius: number;
  /** C08/Wave M: 'burrower' only — seconds spent hidden underground between `special_burrow` finishing and `special_emerge` starting. 0 for every other movementMode (irrelevant — nothing else ever reaches the 'submerged' state that reads this). */
  submergedDuration: number;
}

/**
 * T3-5 (PLAN-CITY2 §2, from Karan's G2 playtest: "monster respawn is way
 * too fast... shouldn't even respawn that fast"): ordinary street enemies
 * (rat/pizza_rat/trash_lobber/pigeon/dive_pigeon), 15-25s — was a flat 6s
 * (this constant's old name/value, `STANDARD_RESPAWN_DELAY`, is preserved
 * in spirit but not in number). 20s is the plan's own stated midpoint.
 */
const STREET_RESPAWN_DELAY = 20;
/**
 * T3-5: `rat_king` — the roaming elite — gets its own, much longer cadence
 * (3 minutes) PLUS the separate "never while on-screen" gate
 * (`EnemyManager.isOnScreen()`, ai/spawner.ts) applied only to this kind.
 * Both halves of the plan's own wording ("3 minutes + never while
 * on-screen") are real, independent gates — this constant is only the
 * timer half.
 */
const ELITE_RESPAWN_DELAY = 180;
/** golden_pigeon only — ~2.5 minutes, not a normal population-refresh cadence. This IS the rarity mechanism (see the archetype's own comment). */
const GOLDEN_PIGEON_RESPAWN_DELAY = 150;

export const ARCHETYPES: Record<EnemyKind, EnemyArchetype> = {
  rat: {
    kind: 'rat',
    label: 'Rat',
    assetId: 'enm_rat',
    scale: 1,
    tint: null,
    maxHp: 40,
    moveSpeed: 3.1,
    radius: 0.35,
    aggroRange: 14,
    attackRange: 1.3,
    telegraphDuration: 0.3,
    attackCooldown: 1.1,
    contactDamage: 8,
    knockbackResist: 0,
    ranged: false,
    preferredMinRange: 0,
    preferredMaxRange: 0,
    projectileDamage: 0,
    projectileSpeed: 0,
    scoreValue: 1,
    movementMode: 'ground',
    requiresLineOfSight: false,
    leashRange: 12,
    flightHeight: 0,
    diveSpeed: 0,
    diveDuration: 0,
    recoverDuration: 0,
    respawnDelay: STREET_RESPAWN_DELAY,
    wanderRadius: 0,
    submergedDuration: 0,
  },
  rat_king: {
    kind: 'rat_king',
    label: 'The Rat King',
    // RETIRED (Wave D-1, PLAN-CITY2 §4): the roaming `elite_patrol` habitat
    // this archetype used to spawn through is DELETED (ai/habitats.ts) — the
    // real Rat King fight now lives entirely inside the basement lair
    // (world/denLair.ts, world/dens.ts's `LairEncounter`), hosting the real
    // `RatKingBoss` class (ai/ratking.ts, its OWN sibling-to-`Boss` FSM, not
    // this `Enemy`-shaped archetype at all). This entry is kept only because
    // `EnemyKind`/`Record<EnemyKind,...>` (loot/droptable.ts et al.) stay
    // exhaustive either way and nothing else in the game still spawns a
    // `kind:'rat_king'` `Enemy` to consume it — it is genuinely inert data,
    // not a live, reachable duplicate of the boss. Superseded history (the
    // S10 asset swap, the Wave M/C08 run-clip fix) preserved in git, not
    // repeated here.
    assetId: 'bos_ratking',
    scale: 1,
    tint: null,
    // Radius/leash/aggro sized against the real 2.3m model (manifest
    // heightMeters) rather than the old 0.6 tuned for a scaled-up 0.52m rat.
    // aggroRange held at 18 (not raised) — combat/autoaim.ts's own AIM_RANGE
    // invariant comment: "must stay >= the largest aggroRange," already
    // exactly 18 to match this same value under the old elite_rat.
    maxHp: 260,
    moveSpeed: 2.4,
    radius: 0.85,
    aggroRange: 18,
    attackRange: 1.9,
    telegraphDuration: 0.5,
    attackCooldown: 1.5,
    contactDamage: 20,
    knockbackResist: 0.75,
    ranged: false,
    preferredMinRange: 0,
    preferredMaxRange: 0,
    projectileDamage: 0,
    projectileSpeed: 0,
    scoreValue: 6,
    movementMode: 'ground',
    requiresLineOfSight: false,
    leashRange: 18,
    flightHeight: 0,
    diveSpeed: 0,
    diveDuration: 0,
    recoverDuration: 0,
    respawnDelay: ELITE_RESPAWN_DELAY,
    wanderRadius: 0,
    submergedDuration: 0,
  },
  trash_lobber: {
    kind: 'trash_lobber',
    label: 'Trash-Can Lobber',
    // S10: C03's real model (validated 2026-08-25, registered but unwired
    // since) replaces the S03 primitive-capsule fallback. heightMeters
    // 0.799 vs the old capsule's own eyeballed 0.9m radius/scale — close
    // enough that no stat retune came with the asset swap.
    assetId: 'enm_trash_lobber',
    scale: 1,
    tint: null,
    maxHp: 55,
    moveSpeed: 2.2,
    radius: 0.45,
    aggroRange: 15,
    attackRange: 11,
    telegraphDuration: 0.4,
    attackCooldown: 1.8,
    contactDamage: 6,
    knockbackResist: 0.2,
    ranged: true,
    preferredMinRange: 6,
    preferredMaxRange: 9,
    projectileDamage: 12,
    projectileSpeed: 9,
    scoreValue: 2,
    movementMode: 'ground',
    // S03: the ranged kiter now genuinely needs a clear sightline, not just
    // a raw distance band — it won't lob a can through a building wall it
    // happens to be standing on the far side of.
    requiresLineOfSight: true,
    leashRange: 12,
    flightHeight: 0,
    diveSpeed: 0,
    diveDuration: 0,
    recoverDuration: 0,
    respawnDelay: STREET_RESPAWN_DELAY,
    wanderRadius: 0,
    submergedDuration: 0,
  },
  pizza_rat: {
    kind: 'pizza_rat',
    label: 'Pizza Rat',
    // S10: C03's real reared-on-haunches model, replacing the primitive
    // capsule fallback — heightMeters 0.55 matches this archetype's existing
    // radius/scale closely enough that no stat retune came with the swap.
    assetId: 'enm_pizza_rat',
    scale: 1,
    tint: null,
    maxHp: 45,
    moveSpeed: 3.4,
    radius: 0.38,
    aggroRange: 13,
    attackRange: 1.3,
    telegraphDuration: 0.28,
    attackCooldown: 1,
    contactDamage: 9,
    knockbackResist: 0,
    ranged: false,
    preferredMinRange: 0,
    preferredMaxRange: 0,
    projectileDamage: 0,
    projectileSpeed: 0,
    scoreValue: 1,
    movementMode: 'ground',
    requiresLineOfSight: false,
    leashRange: 12,
    flightHeight: 0,
    diveSpeed: 0,
    diveDuration: 0,
    recoverDuration: 0,
    respawnDelay: STREET_RESPAWN_DELAY,
    wanderRadius: 0,
    submergedDuration: 0,
  },
  pigeon: {
    kind: 'pigeon',
    label: 'Pigeon',
    // S10: C03's real model. flightHeight (1.2) was already tuned against
    // the manifest's own hoverHeightMeters when this archetype was first
    // authored primitive-only — matches exactly, no change needed there.
    assetId: 'enm_pigeon',
    scale: 1,
    tint: null,
    maxHp: 30,
    moveSpeed: 3.6,
    radius: 0.3,
    aggroRange: 12,
    attackRange: 1.2,
    telegraphDuration: 0.3,
    attackCooldown: 1.3,
    contactDamage: 6,
    knockbackResist: 0,
    ranged: false,
    preferredMinRange: 0,
    preferredMaxRange: 0,
    projectileDamage: 0,
    projectileSpeed: 0,
    scoreValue: 1,
    movementMode: 'flyer',
    requiresLineOfSight: false,
    leashRange: 10,
    flightHeight: 1.2, // C03's own "authored hover height ~1.2m to body center"
    diveSpeed: 0,
    diveDuration: 0,
    recoverDuration: 0,
    respawnDelay: STREET_RESPAWN_DELAY,
    wanderRadius: 0,
    submergedDuration: 0,
  },
  dive_pigeon: {
    kind: 'dive_pigeon',
    label: 'Dive-Bomb Pigeon',
    // S10: C03's real model — same flightHeight-already-matches note as pigeon above (manifest hoverHeightMeters 1.6).
    assetId: 'enm_divepigeon',
    scale: 1.05,
    tint: null,
    maxHp: 35,
    moveSpeed: 2, // slow, lazy hover/circle speed — the dive itself is what's fast
    radius: 0.32,
    aggroRange: 14,
    attackRange: 1.4,
    telegraphDuration: 0.45, // C03: "the player's only warning, make it unmistakable" — a touch longer than a ground bite's
    attackCooldown: 2.5,
    contactDamage: 14, // a real ambush payoff, offset by the long telegraph + recover window below
    knockbackResist: 0.1,
    ranged: false,
    preferredMinRange: 0,
    preferredMaxRange: 0,
    projectileDamage: 0,
    projectileSpeed: 0,
    scoreValue: 3,
    movementMode: 'diver',
    // Won't commit to a dive through a roof/wall it can't actually see the player past.
    requiresLineOfSight: true,
    leashRange: 14,
    flightHeight: 1.6, // C03: "hover a touch higher — it stalks from altitude"
    diveSpeed: 7.5,
    diveDuration: 1.1,
    recoverDuration: 1, // the vulnerable climb-back window — no special defense, just a natural can't-dive-again cooldown
    respawnDelay: STREET_RESPAWN_DELAY,
    wanderRadius: 0,
    submergedDuration: 0,
  },
  golden_pigeon: {
    kind: 'golden_pigeon',
    label: 'Golden Pigeon',
    // S11: "Golden-Pigeon-style rare roamer stub" (PLAN-CITY §5's S11 line) —
    // a shared-asset-reskin of C03's real pigeon model (Classic gotcha
    // #62/#64's idiom, already used in this project for rat_king's own
    // predecessor), not a new GLB. Deliberately harmless (aggroRange 0,
    // contactDamage 0) — it's something to CATCH, not something that fights
    // back, matching the "delightful discovery" fantasy the name evokes
    // rather than a real combat encounter. Genuine rarity comes from
    // `respawnDelay` far below every other archetype's, not from a
    // probability roll — see ai/spawner.ts's own comment on why a per-slot
    // fixed kind (not a per-respawn coin-flip) is what this codebase's
    // pooling model actually supports cleanly.
    assetId: 'enm_pigeon',
    scale: 1.15,
    tint: 0xf6c445, // warm gold — this project's own "special/reward" accent family (matches TOKENS.taxiGold's role in Classic's boss EXPOSED cue)
    maxHp: 22, // less than a normal pigeon (30) — never meant to be a fight
    moveSpeed: 4.4, // faster than a normal pigeon (3.6) — genuinely evasive once spotted, worth the chase
    radius: 0.3,
    aggroRange: 0, // never aggros — always resolves to 'idle'/hoverInPlace in updateChaseOrIdle, regardless of player proximity
    attackRange: 0,
    telegraphDuration: 0,
    attackCooldown: 999,
    contactDamage: 0,
    knockbackResist: 0,
    ranged: false,
    preferredMinRange: 0,
    preferredMaxRange: 0,
    projectileDamage: 0,
    projectileSpeed: 0,
    scoreValue: 4,
    movementMode: 'flyer',
    requiresLineOfSight: false,
    leashRange: 999, // aggroRange 0 means it can never enter a leash-eligible state anyway — kept high rather than 0 so this field still reads as "a free roamer," not an accidental footgun if a future session ever gives it a non-zero aggroRange
    flightHeight: 1.4,
    diveSpeed: 0,
    diveDuration: 0,
    recoverDuration: 0,
    respawnDelay: GOLDEN_PIGEON_RESPAWN_DELAY,
    // S11 review fix: with aggroRange 0 this archetype never leaves the
    // idle branch, so it needs its OWN drift-around-home behavior
    // (ai/enemy.ts's wanderIdle) to actually roam its habitat rather than
    // hover at one fixed spot — 5m comfortably explores the middle of the
    // 12x14m golden_roamer habitat box (ai/habitats.ts) without routinely
    // drifting into its walls/edges.
    wanderRadius: 5,
    submergedDuration: 0,
  },
  sewer_elite: {
    kind: 'sewer_elite',
    label: 'Sewer Elite',
    // C08/Wave M (PLAN-CITY2 §5 M-3, D-3): a shared-asset-reskin of C01's
    // real rat model — Classic gotcha #62/#64's idiom, same technique
    // `golden_pigeon` above already uses for this project. Not the old
    // `elite_rat` naming (that kind was deleted at S10 when `rat_king`
    // moved onto its own real C05 boss model) — this is a NEW, distinct
    // reskin for the water-hole den's own elite.
    assetId: 'enm_rat',
    scale: 1.5, // reads as tougher/bigger than a plain rat (scale 1) without needing new geometry
    tint: 0x5a7a4a, // sickly sewer-green — distinct from golden_pigeon's warm-gold "reward" tint family; this one should read as a threat, not a prize
    maxHp: 90, // an elite, not a boss — meaningfully tougher than a plain rat (40) but nowhere near rat_king's own tier
    moveSpeed: 0, // never chases on the surface — see MovementMode's own 'burrower' doc comment
    radius: 0.45, // scaled up from plain rat's 0.35 to roughly match the 1.5x visual scale
    aggroRange: 10, // a den is a small space — this doesn't need plain rat's open-street 14
    attackRange: 1.6, // the emerge hit-check radius — a bit more generous than plain rat's 1.3, since "resurfaces at your last position" should read as a real threat if you don't move
    telegraphDuration: 0.5, // the one authored tell before burrow (C08's open item: a possible haunch-quiver lead-in baked into special_burrow itself — this is the SEPARATE pre-burrow telegraph, played via the shared 'telegraph1' clip name convention)
    attackCooldown: 4, // cooldown after an emerge (hit or miss) before it can burrow again — long enough that a miss doesn't immediately re-threaten before the player can reposition
    contactDamage: 16, // the emerge burst — between a plain rat's bite (8) and the old elite rat_king's own contact (20), since this is a mid-tier elite, not a boss
    knockbackResist: 0.3,
    ranged: false,
    preferredMinRange: 0,
    preferredMaxRange: 0,
    projectileDamage: 0,
    projectileSpeed: 0,
    scoreValue: 3,
    movementMode: 'burrower',
    requiresLineOfSight: false, // the burrow/emerge cycle has no line-of-sight-gated shot to protect — it teleports, it doesn't lob or dash through walls
    leashRange: 999, // never actually consulted — 'burrower' is excluded from the leash check entirely (ai/enemy.ts's own update() comment on why)
    flightHeight: 0, // stands on the ground between bursts, not hovering
    diveSpeed: 0,
    diveDuration: 0,
    recoverDuration: 0,
    respawnDelay: ELITE_RESPAWN_DELAY, // same long elite cadence as rat_king — this is D-3's own named elite, not a street-population enemy
    wanderRadius: 0,
    submergedDuration: 0.7, // a real pause underground — long enough to feel like a genuine reposition/ambush beat, short enough to stay threatening; first-pass estimate, tune once played in a real den
  },
};
